import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  TableIcon,
  GearIcon,
  GitBranchIcon,
  PlayIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { WorkflowPhase } from '../types/workflowTypes';
import { Button } from 'src/components/ui/button';

interface LinearWorkflowNavigatorProps {
  currentPhase: WorkflowPhase;
  onContinue: () => void;
  onBack: () => void;
  onReset: () => void;
  hidePhaseZero?: boolean;
  isLoading?: boolean; // Loading state for API operations
  fileType?: 'csv' | 'xlsx' | 'image' | null; // File type for dynamic step naming
  // Phase 3 submit functionality
  onSubmit?: () => void; // Submit handler for Phase 3
  isSubmitting?: boolean; // Submitting state
  // Phase 3 validation gating
  canSubmit?: boolean;
  submitDisabledReason?: string;
  // Centralized gating for continue
  canProceed?: boolean;
  continueDisabledReason?: string;
}

const LinearWorkflowNavigator: React.FC<LinearWorkflowNavigatorProps> = ({
  currentPhase,
  onContinue,
  onBack,
  onReset,
  hidePhaseZero = false,
  isLoading = false,
  fileType = null,
  onSubmit,
  isSubmitting = false,
  canSubmit,
  submitDisabledReason,
  canProceed,
  continueDisabledReason,
}) => {
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [backDialogOpen, setBackDialogOpen] = useState(false);

  const handleResetClick = () => {
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    setResetDialogOpen(false);
    onReset();
  };

  const handleResetCancel = () => {
    setResetDialogOpen(false);
  };

  const handleBackClick = () => {
    setBackDialogOpen(true);
  };

  const handleBackConfirm = () => {
    setBackDialogOpen(false);
    // Use setTimeout to ensure the dialog state update happens before the onBack call
    setTimeout(() => {
      onBack();
    }, 0);
  };

  const handleBackCancel = () => {
    setBackDialogOpen(false);
  };
  // Define phase information with dynamic naming for Phase 0 based on file type
  const getPhaseZeroName = () => {
    switch (fileType) {
      case 'xlsx':
        return 'Select Sheet';
      case 'csv':
        return 'Extract Constants';
      case 'image':
        return 'Parse Image';
      default:
        return 'Select Sheet';
    }
  };

  const phases = [
    {
      id: 0,
      name: getPhaseZeroName(),
      icon: TableIcon,
    },
    { id: 1, name: 'Preprocess Data', icon: GearIcon },
    { id: 2, name: 'Map Fields', icon: GitBranchIcon },
    { id: 3, name: 'Run Transformation', icon: PlayIcon },
  ];

  // Filter out Phase 0 if hidePhaseZero is true
  const visiblePhases = hidePhaseZero
    ? phases.filter((p) => p.id !== 0)
    : phases;

  // Adjust current phase index based on visible phases
  const currentPhaseIndex =
    hidePhaseZero && currentPhase > 0 ? currentPhase - 1 : currentPhase;

  // Check if we can go back
  const canGoBack = hidePhaseZero ? currentPhase > 1 : currentPhase > 0;

  // Get continue button label
  const getContinueLabel = () => {
    switch (currentPhase) {
      case 3:
        return 'Complete';
      default:
        return 'Continue';
    }
  };

  // Loading state is now passed as prop

  // Phase step component
  const PhaseStep: React.FC<{
    phase: { id: number; name: string; icon: typeof TableIcon };
    type: 'previous' | 'current' | 'next';
    showLabel: boolean;
    stepNumber: number;
  }> = ({ phase, type, showLabel, stepNumber }) => {
    const getStepClasses = () => {
      switch (type) {
        case 'previous':
          return 'bg-green-100 border-2 border-green-600 text-green-700 opacity-100';
        case 'current':
          return 'bg-blue-50 border-2 border-blue-500 text-blue-500 opacity-100 shadow-md';
        case 'next':
          return 'bg-gray-50 border-2 border-gray-400 text-gray-600 opacity-70';
      }
    };

    const getTextClasses = () => {
      switch (type) {
        case 'previous':
          return 'text-green-700 font-medium';
        case 'current':
          return 'text-blue-500 font-bold';
        case 'next':
          return 'text-gray-600 font-medium';
      }
    };

    const getPillClasses = () => {
      switch (type) {
        case 'previous':
          return 'bg-green-600 text-white';
        case 'current':
          return 'bg-blue-600 text-white';
        case 'next':
          return 'bg-gray-400 text-white';
      }
    };

    return (
      <div className="flex items-center mx-1">
        <div
          className={`flex items-center gap-2 md:gap-3 px-2 py-2 md:px-6 md:py-4 min-w-[120px] md:min-w-[180px] h-14 rounded-lg ${getStepClasses()}`}
        >
          {(showLabel || type === 'current') && (
            <>
              <div
                className={`flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-full font-semibold text-xs md:text-sm shrink-0 ${getPillClasses()}`}
              >
                {stepNumber}
              </div>
              <span className={`whitespace-nowrap text-sm ${getTextClasses()}`}>
                {phase.name}
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="flex items-center justify-between mb-6 py-4 px-6 rounded-lg border border-black">
        {/* Left Side: Reset and Back Buttons */}
        <div className="flex gap-2 min-w-[240px]">
          <Button
            variant="outline"
            onClick={handleResetClick}
            size="default"
            disabled={isLoading}
            className="min-w-[110px] text-red-600 border-red-600 hover:bg-red-50"
          >
            <ArrowCounterClockwiseIcon className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            variant={!canGoBack ? 'outline' : 'default'}
            onClick={handleBackClick}
            size="default"
            disabled={!canGoBack || isLoading}
            className={`min-w-[110px] ${
              !canGoBack
                ? 'text-gray-600 border-gray-300'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <CaretLeftIcon className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Central Phase Navigation - Show All Steps */}
        <div className="flex items-center justify-center flex-1 gap-2 md:gap-6">
          {visiblePhases.map((phase, index) => {
            const isCurrent = index === currentPhaseIndex;
            const isCompleted = phase.id < currentPhase;

            let stepType: 'previous' | 'current' | 'next';
            if (isCurrent) {
              stepType = 'current';
            } else if (isCompleted) {
              stepType = 'previous'; // Completed phases show as "previous" (green)
            } else {
              stepType = 'next'; // Incomplete phases show as "next" (gray)
            }

            const getArrowColor = () => {
              switch (stepType) {
                case 'previous':
                  return 'text-green-600';
                case 'current':
                  return 'text-blue-500';
                case 'next':
                  return 'text-gray-600 opacity-70';
              }
            };

            return (
              <React.Fragment key={phase.id}>
                <PhaseStep
                  phase={phase}
                  type={stepType}
                  showLabel={true}
                  stepNumber={index + 1}
                />
                {index < visiblePhases.length - 1 && (
                  <CaretRightIcon className={`w-4 h-4 ${getArrowColor()}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Continue/Submit Button */}
        <div className="min-w-[120px] flex justify-end">
          {currentPhase === 3 ? (
            // Phase 3: Submit button
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="default"
                    onClick={onSubmit}
                    disabled={!canSubmit || isSubmitting || !onSubmit}
                    size="default"
                    className={`${
                      canSubmit && !isSubmitting
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-gray-400 text-gray-600'
                    }`}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                    <PlayIcon className="w-4 h-4 ml-2" />
                  </Button>
                </span>
              </TooltipTrigger>
              {submitDisabledReason && (
                <TooltipContent>
                  <p>{submitDisabledReason}</p>
                </TooltipContent>
              )}
            </Tooltip>
          ) : (
            // Other phases: Continue button
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="default"
                    onClick={onContinue}
                    disabled={!canProceed}
                    size="default"
                    className={`${
                      !canProceed
                        ? 'bg-gray-400 text-gray-600'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {getContinueLabel()}
                    <CaretRightIcon className="w-4 h-4 ml-2" />
                  </Button>
                </span>
              </TooltipTrigger>
              {!canProceed && continueDisabledReason && (
                <TooltipContent>
                  <p>{continueDisabledReason}</p>
                </TooltipContent>
              )}
            </Tooltip>
          )}
        </div>
      </div>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Workflow</DialogTitle>
            <DialogDescription>
              Are you sure you want to reset the workflow? This will clear all
              your progress and data, and return you to the file upload step.
              <br />
              <br />
              <strong>This action cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={handleResetCancel}
              color="secondary"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetConfirm}
              variant="default"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <ArrowCounterClockwiseIcon className="w-4 h-4 mr-2" />
              Reset Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Back Confirmation Dialog */}
      <Dialog open={backDialogOpen} onOpenChange={setBackDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Go Back to Previous Phase</DialogTitle>
            <DialogDescription>
              Are you sure you want to go back to the previous phase? This will
              reset the current phase and all subsequent phases, clearing any
              progress made.
              <br />
              <br />
              <strong>
                You will need to redo the work from this point forward.
              </strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={handleBackCancel}
              color="secondary"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBackConfirm}
              variant="default"
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              <CaretLeftIcon className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default LinearWorkflowNavigator;
